import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  CaretDownIcon,
  ListIcon,
  SignInIcon,
  SignOutIcon,
  UserIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useAuth } from '../../../shared/ui/AuthProvider';
import { getInitials } from '../utils/format';

interface AppHeaderSpaProps {
  menuOpen?: boolean;
  onToggleMenu?: () => void;
}

export const AppHeaderSpa: React.FC<AppHeaderSpaProps> = ({
  menuOpen = false,
  onToggleMenu,
}) => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setProfileMenuOpen(false);
    await logout();
    navigate({ to: '/login' });
  };

  const currentLang = i18n.language.startsWith('de') ? 'de' : 'en';

  const avatar = (
    <span className="mc-avatar">{getInitials(user ?? undefined)}</span>
  );

  const languageSelect = (
    <div
      className="mc-lang-switch"
      role="group"
      aria-label={t('app.nav.language')}
    >
      {(['de', 'en'] as const).map(lang => (
        <button
          key={lang}
          type="button"
          lang={lang}
          aria-current={currentLang === lang ? 'true' : undefined}
          onClick={() => i18n.changeLanguage(lang)}
        >
          {lang.toUpperCase()}
        </button>
      ))}
    </div>
  );

  return (
    <header className="mc-topbar">
      <div className="mc-topbar__left">
        {isAuthenticated && (
          <button
            type="button"
            className="mc-menu-toggle"
            onClick={onToggleMenu}
            aria-label={t('app.nav.menu')}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <XIcon size={22} /> : <ListIcon size={22} />}
          </button>
        )}
        <Link to="/">
          <img
            src="/images/mietclub-logo.png"
            alt="The MietClub"
            className="mc-topbar__logo"
          />
        </Link>
      </div>

      <div className="mc-topbar__right">
        {languageSelect}

        {isAuthenticated ? (
          <div className="mc-user" ref={profileMenuRef}>
            <button
              type="button"
              className="mc-user__trigger"
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              aria-expanded={profileMenuOpen}
            >
              {avatar}
              <span className="mc-user__name">
                {[user?.firstName, user?.lastName].filter(Boolean).join(' ')}
              </span>
              <CaretDownIcon size={14} />
            </button>
            {profileMenuOpen && (
              <div className="mc-user__menu">
                <div className="mc-user__info">
                  <strong>
                    {user?.firstName} {user?.lastName}
                  </strong>
                  <span>{user?.email}</span>
                </div>
                <Link
                  to="/profile"
                  className="mc-nav"
                  onClick={() => setProfileMenuOpen(false)}
                >
                  <span className="mc-nav__icon">
                    <UserIcon size={20} />
                  </span>
                  {t('app.nav.profile')}
                </Link>
                <button type="button" className="mc-nav" onClick={handleLogout}>
                  <span className="mc-nav__icon">
                    <SignOutIcon size={20} />
                  </span>
                  {t('app.nav.logout')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <Link to="/login" className="mc-btn mc-btn--outline mc-login-link">
            <SignInIcon size={18} />
            {t('header.login')}
          </Link>
        )}
      </div>
    </header>
  );
};
